import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ILinkedUnit, IMsoolUnit } from '@services';
import { ChannelType } from '../../../../../services/useChannels';
import {
  UnitsList,
  TableHeader,
  TableHeaderCell,
  UnitRow,
  UnitInfoCell,
  UnitName,
  StatusBadge,
  StatusDot,
} from '../../../styles/UnitsMappingStyles';

// Import unified channel logo components
import { ChannelLogo, MsoolLogo, ChannelLogoType } from '../../ChannelLogos';
import { T_KEYS } from '../../../constants';
import PlaceholderImage from '../PlaceholderImage';

const CardContainer = styled(Box)({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  border: '1px solid #e6e6e6',
  marginBottom: '24px',
  overflow: 'clip',
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
  padding: '1px',
});

const CardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
  backgroundColor: '#ffffff',
});

const HeaderRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  justifyContent: 'flex-start',
});

const MsoolBadge = styled(Box)({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const HeaderTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#1a1a1a',
  lineHeight: '24px',
});

const TableContainer = styled(Box)({
  width: '100%',
  padding: '16px',
});

const TableContent = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
});

const TableColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  flex: '1 0 0',
  minWidth: '0',
  minHeight: '1px',
});

const PropertyColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '600px',
});

const TableHeaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingBottom: '4px',
  width: '100%',
});

const TableHeaderTextContainer = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  padding: '8px 8px 4px 8px',
  width: '100%',
});

const TableHeaderText = styled(Typography)({
  fontSize: '12px',
  fontWeight: 700,
  color: '#37352f',
  fontFamily: '"Tajawal", sans-serif',
  textAlign: 'start',
  padding: '0 8px',
});

const Divider = styled(Box)({
  height: '1px',
  width: '100%',
  backgroundColor: '#e9e9e7',
});

const TableRowItem = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  height: '50px',
  padding: '14px 0',
  borderBottom: '1px solid #e9e9e7',
  '&:last-child': {
    borderBottom: 'none',
  },
});

const TableRowContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '2px 8px',
});

const PropertyRowContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  height: '50px',
  padding: '8px 0',
  width: '100%',
  borderBottom: '1px solid #e9e9e7',
  '&:last-child': {
    borderBottom: 'none',
  },
});

const PropertyRowContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  paddingInlineEnd: '16px',
});

const PropertyInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const PropertyImage = styled(Box)({
  width: '63px',
  height: '30px',
  borderRadius: '4px',
  overflow: 'hidden',
  position: 'relative',
  flexShrink: 0,
  backgroundColor: '#f5f5f5',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

const PropertyName = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: '#331860',
  fontFamily: '"Tajawal", sans-serif',
  textAlign: 'start',
  whiteSpace: 'nowrap',
});

const ChannelsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// Note: ChannelLogoWrapper is no longer used - using unified ChannelLogo component instead


const UnmapButton = styled(Button)({
  color: '#5b3fff',
  fontSize: '12px',
  fontWeight: 400,
  textTransform: 'none',
  padding: '2px 8px',
  borderRadius: '9999px',
  border: '1px solid #5b3fff',
  backgroundColor: 'transparent',
  width: '77px',
  height: '25px',
  lineHeight: '16px',
  '&:hover': {
    backgroundColor: 'rgba(91, 63, 255, 0.04)',
    border: '1px solid #5b3fff',
  },
});

const EmptyState = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px',
  color: '#999999',
});

// Custom Radio Button to match Figma design
const CustomRadio = styled('div')<{ $checked: boolean }>(({ $checked }) => ({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  backgroundColor: $checked ? '#5b3fff' : 'transparent',
  border: $checked ? 'none' : '1.5px solid #bfbfbf',
  transition: 'all 0.2s ease',
  flexShrink: 0,
  '&:hover': {
    borderColor: '#5b3fff',
  },
}));

const RadioInnerDot = styled('div')({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: 'white',
});

interface MsoolUnitsCardProps {
  units: IMsoolUnit[];
  isLoading?: boolean;
  selectedUnit: IMsoolUnit | null;
  onUnmap: (msoolUnit: IMsoolUnit) => void;
  onSelectUnit: (msoolUnit: IMsoolUnit) => void;
}

const getChannelLogoType = (channelType: ChannelType): ChannelLogoType | null => {
  switch (channelType) {
    case ChannelType.Airbnb:
      return 'airbnb';
    case ChannelType.Booking:
      return 'booking';
    case ChannelType.Gathern:
      return 'gathern';
    default:
      return null;
  }
};

const renderChannelLogo = (channelType: ChannelType, index: number) => {
  const logoType = getChannelLogoType(channelType);
  if (!logoType) return null;
  
  return (
    <ChannelLogo key={`${logoType}-${index}`} channel={logoType} size="sm" rounded />
  );
};

const MsoolUnitsCard: React.FC<MsoolUnitsCardProps> = memo(({ units, isLoading, selectedUnit, onUnmap, onSelectUnit }) => {
  const { t } = useTranslation();

  const handleUnmap = useCallback((msoolUnit: IMsoolUnit) => {
    onUnmap(msoolUnit);
  }, [onUnmap]);

  const handleSelectUnit = useCallback((msoolUnit: IMsoolUnit) => {
    onSelectUnit(msoolUnit);
  }, [onSelectUnit]);

  const isUnitSelected = useCallback((unit: IMsoolUnit) => {
    return selectedUnit?.propertyId === unit.propertyId;
  }, [selectedUnit]);

  if (!units || units.length === 0) {
    return null;
  }

  return (
    <CardContainer>
      <CardHeader>
        <HeaderRight>
          <MsoolBadge>
            <MsoolLogo size="sm" rounded />
          </MsoolBadge>
          <HeaderTitle>
            {t(T_KEYS.UNITS_MAPPING_MSOOL_UNITS_TITLE)}
          </HeaderTitle>
        </HeaderRight>
        <Box sx={{ opacity: 0 }}>
          <Typography sx={{ fontSize: '14px', color: '#7a7a7a',  lineHeight: '20px' }}>
            {units.length} {t(T_KEYS.UNITS_MAPPING_UNIT)}
          </Typography>
        </Box>
      </CardHeader>

      <UnitsList>
        <TableHeader>
          <TableHeaderCell sx={{ width: '600px' }}>{t(T_KEYS.UNITS_MAPPING_PROPERTY)}</TableHeaderCell>
          <TableHeaderCell sx={{ width: '240px' }}>{t(T_KEYS.UNITS_MAPPING_CHANNELS)}</TableHeaderCell>
          <TableHeaderCell sx={{ width: '240px' }}>{t(T_KEYS.UNITS_MAPPING_ACTIONS)}</TableHeaderCell>
        </TableHeader>
        {units.map((unit) => {
          const isSelected = isUnitSelected(unit);
          const hasLinkedChannels = unit.channels?.some((channel: ILinkedUnit) => channel.status);
          // Check if unit has non-Airbnb channels connected (Booking or Gathern)
          const hasNonAirbnbChannels = unit.channels?.some(
            (channel: ILinkedUnit) => channel.status && channel.channelType !== ChannelType.Airbnb
          );
          // Only show unmap button if there are non-Airbnb channels connected
          const showUnmapButton = hasLinkedChannels && hasNonAirbnbChannels;
          
          return (
            <UnitRow 
              key={unit.propertyId} 
              $isSelected={isSelected} 
              $isMapped={false}
              onClick={() => handleSelectUnit(unit)}
              sx={{ cursor: 'pointer' }}
            >
              {/* Property Column - Radio, Image, Name */}
              <UnitInfoCell sx={{ width: '600px' }}>
                <CustomRadio $checked={isSelected}>
                  {isSelected && <RadioInnerDot />}
                </CustomRadio>
                <PlaceholderImage
                  src={unit.imageUrl || unit.channels?.[0]?.listingImage}
                  alt={unit.propertyName ?? ''}
                  fallbackText={unit.propertyName?.charAt(0)}
                />
                <UnitName>{unit.propertyName}</UnitName>
              </UnitInfoCell>

              {/* Channels Column */}
              <Box sx={{ width: '240px' }}>
                <ChannelsContainer>
                  {unit.channels?.filter((channel: ILinkedUnit) => channel.status)?.map((linkedUnit, index) => 
                    renderChannelLogo(linkedUnit.channelType, index)
                  )}
                </ChannelsContainer>
              </Box>

              {/* Action Column - Only show unmap if non-Airbnb channels are connected */}
              <Box sx={{  display: 'flex', justifyContent: 'center' }}>
                {showUnmapButton && (
                  <UnmapButton onClick={(e) => { e.stopPropagation(); handleUnmap(unit); }}>
                    {t(T_KEYS.UNITS_MAPPING_UNMAP)}
                  </UnmapButton>
                )}
              </Box>
            </UnitRow>
          );
        })}
      </UnitsList>

      {units.length === 0 && (
        <EmptyState>
          <Typography>
            {t(T_KEYS.UNITS_MAPPING_NO_MAPPED)}
          </Typography>
        </EmptyState>
      )}
    </CardContainer>
  );
});

MsoolUnitsCard.displayName = 'MsoolUnitsCard';

export default MsoolUnitsCard;
